import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import stockimg2 from "../assets/stockimg8.jpg";
import stockimg3 from "../assets/stockimg3.jpg";
import stockimg4 from "../assets/stockimg4.jpg";
import stockimg5 from "../assets/stockimg5.jpg";
import { useNavigate } from "react-router-dom";

// Reusable SVG icons styled to match HTML design
const CheckIcon = () => (
  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const UserAddIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

function Content() {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const animationVariants = useMemo(() => ({
    container: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.2,
          delayChildren: 0.2
        }
      }
    },
    item: {
      hidden: { y: 25, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          duration: 0.5,
          ease: "easeOut"
        }
      }
    },
    imageAnim: {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.7,
          ease: "easeOut"
        }
      }
    }
  }), []);

  const galleryImages = useMemo(() => [
    { src: "/img/fundamental_research.webp", title: "In-Depth Fundamental Research" },
    { src: "/img/technical_indicators.webp", title: "Real-Time Technical Indicators" },
    { src: "/img/market_tracking_sq_v2.jpg", title: "Data-Driven Market Tracking" },
    { src: "/img/portfolio_allocation_sq_v2.jpg", title: "Strategic Portfolio Allocation" },
  ], []);

  return (
    <div className="bg-slate-50 text-slate-800 antialiased font-['Plus_Jakarta_Sans',sans-serif]">
      {/* Embedded keyframe animations matching the original HTML style tag */}
      <style>{`
        @keyframes shine {
          to { background-position: 200% center; }
        }
        .gradient-text-animated {
          background: linear-gradient(135deg, #059669 0%, #10B981 50%, #D97706 100%);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shine 4s linear infinite;
        }

        @keyframes float3d {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(1deg); }
        }
        .animate-float-card {
          animation: float3d 6s ease-in-out infinite;
        }

        .perspective-1000 {
          perspective: 1000px;
        }
        .preserve-3d {
          transform-style: preserve-3d;
          transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.5s ease;
        }
        .preserve-3d:hover {
          transform: translateY(-8px) rotateX(4deg) rotateY(-3deg);
        }
      `}</style>

      {/* Hero / About Content Section */}
    <section className="relative py-8 overflow-hidden bg-gradient-to-b from-emerald-50/60 via-white to-slate-50">        {/* Soft Ambient Background Circles */}
        <div className="absolute -top-10 -left-10 w-80 h-80 bg-emerald-200/40 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-100/60 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Copy Column */}
            <motion.div
              className="lg:col-span-7 space-y-6"
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              variants={animationVariants.container}
            >
              <motion.div variants={animationVariants.item}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-100/80 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-200">
                  Meet Your Financial Allies
                </span>
              </motion.div>

              <motion.h2
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight"
                variants={animationVariants.item}
              >
                The Brains Behind{" "}
                <span className="gradient-text-animated">
                  Your Success
                </span>
              </motion.h2>

             

              {/* Feature Bullet Highlights */}
              <motion.div className="space-y-4 pt-2" variants={animationVariants.container}>
                <motion.div className="flex items-start gap-3" variants={animationVariants.item}>
                  <div className="flex-shrink-0 mt-1 w-6 h-6 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                    <CheckIcon />
                  </div>
                  <p className="text-sm sm:text-base text-slate-700 font-medium">
                    <strong className="text-slate-900">Our Approach: </strong> Our elite team delivers real-time insights for competitive edge.
                  </p>
                </motion.div>

                
              </motion.div>

              {/* Action Button */}
              <motion.div className="pt-4" variants={animationVariants.item}>
                <button
                  onClick={() => navigate("/contactus")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/25 hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
                >
                  <UserAddIcon />
                  Join Our Team
                </button>
              </motion.div>
            </motion.div>

            {/* Right Card Column with 3D Float Animation */}
            <motion.div
              className="lg:col-span-5 perspective-1000"
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              variants={animationVariants.imageAnim}
            >
              <div className="preserve-3d animate-float-card bg-white/90 border border-slate-200/80 backdrop-blur-xl p-4 sm:p-6 rounded-3xl shadow-2xl relative group">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-green-600 rounded-t-3xl"></div>

                <div className="relative rounded-2xl overflow-hidden shadow-md h-72 sm:h-80 group">
                  <img
                    src={stockimg2}
                    alt="Team Collaboration"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent flex items-end p-6">
                    <p className="text-white font-bold text-lg sm:text-xl leading-snug group-hover:text-emerald-300 transition-colors">
                      "Our team lives and breathes your financial success."
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

 

      {/* 3D Visual Gallery Grid */}
     <section className="py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {galleryImages.map((item, idx) => (
              <div key={idx} className="perspective-1000">
                <div className="preserve-3d relative rounded-3xl overflow-hidden shadow-lg h-64 border border-slate-200 group bg-slate-900">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent flex items-end p-6">
                    <h4 className="text-base sm:text-lg font-bold text-white group-hover:text-emerald-300 transition-colors">
                      {item.title}
                    </h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default React.memo(Content);